declare const RegExp: RegExpConstructor & {
  escape(s: string): string;
};

export const normaliseForSearch = (s: string) =>
  s
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase();

type StringPropertyNames<T> = {
  [K in keyof T]: T[K] extends string | null | undefined ? K : never;
}[keyof T];

export function filterAndSortByValue(items: string[], searchTerm: string): string[];
export function filterAndSortByValue<T extends {}>(
  items: Exclude<T, string>[],
  searchTerm: string,
  ...properties: [StringPropertyNames<T>, ...StringPropertyNames<T>[]]
): T[];
export function filterAndSortByValue(
  items: any[],
  searchTerm: string,
  ...properties: string[]
): any[] {
  if (!searchTerm?.trim()) {
    return items;
  }

  if (properties.length === 0) {
    return filterAndSortByValueImpl(items, searchTerm, x => [normaliseForSearch(x ?? '')]);
  }

  return filterAndSortByValueImpl(items, searchTerm, x =>
    properties.map(k => normaliseForSearch(x[k] ?? '')),
  );
}

const filterAndSortByValueImpl = <T extends {}>(
  items: T[],
  searchTerm: string,
  getNormalisedValues: (x: T) => string[],
): T[] => {
  const searchTermRegex = new RegExp(RegExp.escape(normaliseForSearch(searchTerm)), 'i');

  return items
    .filter(x => {
      for (const value of getNormalisedValues(x)) {
        if (value && searchTermRegex.test(value)) {
          return true;
        }
      }
      return false;
    })
    .sort((a, b) => {
      // both will have same length
      const aValues = getNormalisedValues(a);
      const bValues = getNormalisedValues(b);
      for (let i = 0; i < aValues.length; i++) {
        const aValue = aValues[i];
        const bValue = bValues[i];

        if (aValue === bValue) {
          continue;
        }

        const aIndex = searchTermRegex.exec(aValue)?.index ?? -1;
        const bIndex = searchTermRegex.exec(bValue)?.index ?? -1;

        // Prefer start of word over middle
        if (aIndex > 0 && bIndex > 0) {
          const aIsStart = aValue[aIndex - 1] === ' ';
          const bIsStart = bValue[bIndex - 1] === ' ';

          if (aIsStart !== bIsStart) {
            return aIsStart ? -1 : 1;
          }
        }

        if (aIndex !== bIndex) {
          if (aIndex === -1 || bIndex === -1) {
            return aIndex === -1 ? 1 : -1;
          }
          return aIndex - bIndex;
        }
      }

      return 0;
    });
};
